import React, { useMemo } from 'react';
import { MACRO_CHART_COLORS } from '@/constants/macro-chart-colors';
import { cn } from '@/lib/utils';
import { formatMacro } from '@/utils/gram-ml';

export interface MacroDonutWithLegendProps {
  carbs: number;
  protein: number;
  fat: number;
  /** Grams of fibre; used when `fourthEnergyNutrient` is `'fiber'` (default). */
  fiber?: number;
  /** Grams of alcohol; used when `fourthEnergyNutrient` is `'alcohol'`. */
  alcohol?: number;
  /**
   * Fourth arc: either fibre at 2 kcal/g (legacy) or alcohol at 7 kcal/g (custom recipe breakdown).
   * Percentages and arc sizes use only carbs×4 + protein×4 + fat×9 + fourth kcal.
   */
  fourthEnergyNutrient?: 'fiber' | 'alcohol';
  /** Optional title above the donut + legend row */
  title?: string;
  /** Heading above the legend list (default: Macronutrients) */
  legendHeading?: string;
  fiberLabel?: 'Fiber' | 'Fibre';
  className?: string;
  /**
   * When set, shown in the donut center instead of macro-derived kcal sum
   * (e.g. declared calories per serving so the ring + legend stay macro-based but the headline matches the label).
   */
  centerPrimaryOverride?: string;
  /** Second line under center number (default: kcal) */
  centerUnitLabel?: string;
  /** Scales the SVG donut; `large` is suited for recipe modal / wide layouts */
  donutVisualSize?: 'default' | 'large';
  /**
   * `inline` — donut left, legend right (default, e.g. recipe editor sidebar).
   * `splitHalf` — two equal columns: chart | macro values (e.g. recipe details modal).
   * `stacked` — donut above macro values (e.g. side-by-side nutrition comparisons).
   */
  contentLayout?: 'inline' | 'splitHalf' | 'stacked';
  /** When false, hide the fourth nutrient (fiber/alcohol) from both donut and legend. */
  showFourthNutrient?: boolean;
}

function MacroDonutSvg({
  carbs,
  protein,
  fat,
  fourthGrams,
  fourthEnergyType,
  showFourthNutrient,
  centerLine1,
  centerLine2,
  visualSize,
}: {
  carbs: number;
  protein: number;
  fat: number;
  fourthGrams: number;
  fourthEnergyType: 'fiber' | 'alcohol';
  showFourthNutrient: boolean;
  centerLine1: string;
  centerLine2: string;
  visualSize: 'default' | 'large';
}) {
  const { segments, totalMacroCals } = useMemo(() => {
    const carbC = Math.max(0, carbs) * 4;
    const protC = Math.max(0, protein) * 4;
    const fatC = Math.max(0, fat) * 9;
    const fourthKcalPerG = fourthEnergyType === 'alcohol' ? 7 : 2;
    const fourthC = showFourthNutrient ? Math.max(0, fourthGrams) * fourthKcalPerG : 0;
    const t = carbC + protC + fatC + fourthC;
    if (t <= 0) {
      return {
        segments: [] as { pct: number; color: string; val: number }[],
        totalMacroCals: 0,
      };
    }
    const fourthColor =
      fourthEnergyType === 'alcohol' ? MACRO_CHART_COLORS.alcohol : MACRO_CHART_COLORS.fiber;
    const validSegments = [
      { pct: fatC / t, color: MACRO_CHART_COLORS.fat, val: fatC },
      { pct: protC / t, color: MACRO_CHART_COLORS.protein, val: protC },
      { pct: carbC / t, color: MACRO_CHART_COLORS.carbs, val: carbC },
      { pct: fourthC / t, color: fourthColor, val: fourthC },
    ].filter((s) => s.val > 0);

    return {
      segments: validSegments,
      totalMacroCals: t,
    };
  }, [carbs, protein, fat, fourthGrams, fourthEnergyType, showFourthNutrient]);

  const strokeW = 6.68;
  const r = 40 - strokeW / 2;
  const c = 2 * Math.PI * r;
  const gap = segments.length > 1 ? strokeW + 2 : 0;
  let dashOffset = 0;

  const frameClass =
    visualSize === 'large' ? 'h-[108px] w-[108px]' : 'h-[82.34px] w-[82.34px]';
  const centerPrimaryClass =
    visualSize === 'large'
      ? 'text-[18px] leading-[20px] tracking-[-0.36px]'
      : 'text-[15.6px] leading-[15.6px] tracking-[-0.31px]';
  const centerSecondaryClass =
    visualSize === 'large' ? 'text-xs leading-[12px]' : 'text-xs leading-[12px]';

  return (
    <div className={cn('relative flex shrink-0 items-center justify-center', frameClass)}>
      <svg viewBox="0 0 80 80" className="h-full w-full -rotate-90" aria-hidden>
        <circle cx="40" cy="40" r={r} fill="none" stroke="#f3f4f6" strokeWidth={strokeW} />
        {totalMacroCals > 0
          ? segments.map((seg, i) => {
              const arcLen = seg.pct * c;
              const visualArcLen = Math.max(0.001, arcLen - gap);
              const el = (
                <circle
                  key={i}
                  cx="40"
                  cy="40"
                  r={r}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={strokeW}
                  strokeLinecap="round"
                  strokeDasharray={`${visualArcLen} ${c - visualArcLen}`}
                  strokeDashoffset={-dashOffset}
                />
              );
              dashOffset += arcLen;
              return el;
            })
          : null}
      </svg>
      <div className="pointer-events-none absolute inset-0 mt-[1.5px] flex flex-col items-center justify-center gap-[1.7px] text-center">
        <span className={cn('font-medium text-[#111827]', centerPrimaryClass)}>{centerLine1}</span>
        <span
          className={cn('font-medium tracking-[0.49px] text-[#9ca3af]', centerSecondaryClass)}
        >
          {centerLine2}
        </span>
      </div>
    </div>
  );
}

function MacroLegendRow({
  color,
  pctLabel,
  gramsLabel,
  name,
}: {
  color: string;
  pctLabel: string;
  gramsLabel: string;
  name: string;
}) {
  return (
    <div className="flex gap-2.5 border-b border-[#f1f5f9] py-2 first:pt-0 last:border-b-0 last:pb-0">
      <span className="mt-1.5 size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[13px] font-medium text-[#374151]">{name}</span>
          <span className="shrink-0 text-[13px] font-semibold tabular-nums text-[#111827]">{gramsLabel}</span>
        </div>
        <p className="mt-0.5 text-xs font-semibold tabular-nums leading-tight" style={{ color }}>
          {pctLabel}
          <span className="ml-1 font-normal text-[#94a3b8]">of macro energy</span>
        </p>
      </div>
    </div>
  );
}

/**
 * SVG macro donut + legend — same visuals as recipe `NutritionTotals` (custom recipe editor).
 * Default fourth nutrient is fibre at 2 kcal/g; use `fourthEnergyNutrient="alcohol"` with `alcohol` grams for 7 kcal/g.
 */
export const MacroDonutWithLegend: React.FC<MacroDonutWithLegendProps> = ({
  carbs,
  protein,
  fat,
  fiber = 0,
  alcohol = 0,
  fourthEnergyNutrient = 'fiber',
  title,
  legendHeading = 'Macronutrients',
  fiberLabel = 'Fiber',
  className,
  centerPrimaryOverride,
  centerUnitLabel = 'kcal',
  donutVisualSize = 'default',
  contentLayout = 'inline',
  showFourthNutrient = true,
}) => {
  const fourthGrams = fourthEnergyNutrient === 'alcohol' ? alcohol : fiber;
  const fourthKcalPerG = fourthEnergyNutrient === 'alcohol' ? 7 : 2;
  const fourthKcal = showFourthNutrient ? Math.max(0, fourthGrams) * fourthKcalPerG : 0;

  const carbC = Math.max(0, carbs) * 4;
  const protC = Math.max(0, protein) * 4;
  const fatC = Math.max(0, fat) * 9;
  const denom = carbC + protC + fatC + fourthKcal;

  const carbPct = denom > 0 ? Number(((carbC / denom) * 100).toFixed(2)) : 0;
  const protPct = denom > 0 ? Number(((protC / denom) * 100).toFixed(2)) : 0;
  const fatPct = denom > 0 ? Number(((fatC / denom) * 100).toFixed(2)) : 0;
  const fourthPct = denom > 0 ? Number(((fourthKcal / denom) * 100).toFixed(2)) : 0;

  const macroKcalStr = formatMacro(denom);
  const centerLine1 = centerPrimaryOverride ?? macroKcalStr;

  const donutEl = (
    <MacroDonutSvg
      carbs={carbs}
      protein={protein}
      fat={fat}
      fourthGrams={fourthGrams}
      fourthEnergyType={fourthEnergyNutrient}
      showFourthNutrient={showFourthNutrient}
      centerLine1={centerLine1}
      centerLine2={centerUnitLabel}
      visualSize={donutVisualSize}
    />
  );

  const fourthLegendName = fourthEnergyNutrient === 'alcohol' ? 'Alcohol' : fiberLabel;
  const fourthLegendColor =
    fourthEnergyNutrient === 'alcohol' ? MACRO_CHART_COLORS.alcohol : MACRO_CHART_COLORS.fiber;

  const legendEl = (
    <div
      className={cn(
        'flex min-w-0 flex-col justify-center',
        contentLayout === 'inline' ? 'min-w-0 flex-1 py-[11px]' : 'py-1 sm:py-0',
      )}
    >
      <p
        className={cn(
          'text-xs font-bold uppercase tracking-[0.9px] text-[#94a3b8]',
          contentLayout === 'inline' ? 'mb-[13px]' : 'mb-3',
        )}
      >
        {legendHeading}
      </p>
      <div className={cn('flex w-full flex-col', contentLayout === 'inline' ? 'gap-[6px]' : 'gap-1')}>
        <MacroLegendRow
          color={MACRO_CHART_COLORS.carbs}
          pctLabel={`${carbPct}%`}
          gramsLabel={`${formatMacro(carbs)}g`}
          name="Carbs"
        />
        <MacroLegendRow
          color={MACRO_CHART_COLORS.protein}
          pctLabel={`${protPct}%`}
          gramsLabel={`${formatMacro(protein)}g`}
          name="Protein"
        />
        <MacroLegendRow
          color={MACRO_CHART_COLORS.fat}
          pctLabel={`${fatPct}%`}
          gramsLabel={`${formatMacro(fat)}g`}
          name="Fat"
        />
        {showFourthNutrient ? (
          <MacroLegendRow
            color={fourthLegendColor}
            pctLabel={`${fourthPct}%`}
            gramsLabel={`${formatMacro(fourthGrams)}g`}
            name={fourthLegendName}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={cn('w-full', className)} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {title ? <h4 className="mb-2 text-sm font-medium text-[#111827]">{title}</h4> : null}
      {contentLayout === 'splitHalf' ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:items-center sm:gap-0">
          <div className="flex min-h-[120px] min-w-0 items-center justify-center sm:min-h-[140px] sm:pr-6">
            {donutEl}
          </div>
          <div className="min-w-0 border-t border-[#f1f5f9] pt-6 sm:border-l sm:border-t-0 sm:pl-8 sm:pt-0">
            {legendEl}
          </div>
        </div>
      ) : contentLayout === 'stacked' ? (
        <div className="flex min-w-0 flex-col">
          <div className="flex min-h-[118px] min-w-0 items-center justify-center">
            {donutEl}
          </div>
          <div className="mt-4 min-w-0 border-t border-[#f1f5f9] pt-4">
            {legendEl}
          </div>
        </div>
      ) : (
        <div className="flex w-full items-center gap-[15px]">
          {donutEl}
          {legendEl}
        </div>
      )}
    </div>
  );
};
